import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Plus, Building2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { listAccessibleTenants, type AccessibleTenant } from "@/lib/admin-tenants";

export const Route = createFileRoute("/admin/tenants")({
  head: () => ({ meta: [{ title: "Admin · Tenants — ZYNTRA" }] }),
  component: AdminTenantsLayout,
});

function AdminTenantsLayout() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [tenants, setTenants] = useState<AccessibleTenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // Are we on the index (list) or on a child route?
  const isIndex = location.pathname === "/admin/tenants" || location.pathname === "/admin/tenants/";

  async function refresh() {
    try {
      const data = await listAccessibleTenants();
      setTenants(data);
      setIsPlatformAdmin(data.some((t) => t.my_role === "platform_admin"));
    } catch (e: any) {
      setError(e?.message ?? "Error al cargar tenants.");
      setTenants([]);
    }
  }

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setTenants([]);
      return;
    }
    refresh();
  }, [user, authLoading]);

  async function createTenant(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !newSlug.trim()) return;
    setCreating(true);
    setError(null);
    const { data: tenant, error: tenantErr } = await supabase
      .from("tenants")
      .insert({ name: newName.trim(), slug: newSlug.trim().toLowerCase(), status: "active" })
      .select("id")
      .single();
    if (tenantErr || !tenant) {
      setError(tenantErr?.message ?? "No se pudo crear el tenant.");
      setCreating(false);
      return;
    }
    // seed empty branding + settings rows
    await supabase.from("tenant_branding").insert({
      tenant_id: tenant.id,
      brand_name: newName.trim(),
    });
    await supabase.from("tenant_settings").insert({ tenant_id: tenant.id });
    setNewName("");
    setNewSlug("");
    setCreating(false);
    await refresh();
  }

  if (authLoading || tenants === null) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || tenants.length === 0) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Este panel está disponible solo para administradores de tenant.
        </p>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">
          Volver al dashboard
        </Link>
      </div>
    );
  }

  // Sub-route active: render Outlet only (the child page provides its own back link)
  if (!isIndex) {
    return <Outlet />;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 pb-20 pt-24 sm:px-6">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al dashboard
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tenants</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestioná las marcas, branding visual, configuración y miembros de cada tenant.
          </p>
        </div>
      </div>

      {error && (
        <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
      )}

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tenants.map((t) => (
          <Link
            key={t.id}
            to="/admin/tenants/$tenantId"
            params={{ tenantId: t.id }}
            className="group rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/50"
          >
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Building2 className="h-5 w-5" />
              </div>
              {t.is_default && (
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                  Default
                </span>
              )}
            </div>
            <p className="mt-3 text-base font-bold text-foreground">{t.name}</p>
            <p className="text-xs text-muted-foreground">/{t.slug}</p>
            <div className="mt-3 flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                t.status === "active"
                  ? "bg-success/10 text-success"
                  : "bg-muted text-muted-foreground"
              }`}>
                {t.status}
              </span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                {t.my_role === "platform_admin" ? "Platform admin" : t.my_role}
              </span>
            </div>
          </Link>
        ))}
      </div>

      {isPlatformAdmin && (
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-card/50 p-5">
          <p className="flex items-center gap-2 text-sm font-bold text-foreground">
            <Plus className="h-4 w-4" /> Crear nuevo tenant
          </p>
          <form onSubmit={createTenant} className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <input
              type="text"
              placeholder="Nombre (ej: Aurora Productions)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              required
            />
            <input
              type="text"
              placeholder="slug (ej: aurora)"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              required
            />
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg gradient-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Crear
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
